import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, NgClass, NgFor, NgIf } from '@angular/common';
import { ToastController, IonImg, IonProgressBar } from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UserCollectionsServices } from '../../providers/user-collection-services';
import { GlobalDataService } from '../../providers/global-data.service';
import { BtnComponent } from '../btn/btn.component';
import { addIcons } from 'ionicons';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
@Component({
  selector: 'app-v2-open-binders',
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    NgFor,
    NgClass,
    TranslatePipe,
    IonImg,
    IonProgressBar,
    BtnComponent,
  ],
  templateUrl: './v2-open-binders.component.html',
  styleUrls: ['./v2-open-binders.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2OpenBindersComponent implements OnChanges, OnDestroy {
  @ViewChild('overlayRoot', { read: ElementRef }) overlayRootEl?: ElementRef<HTMLDivElement>;

  @Input() isOpen = false;
  @Input() agentId = '';
  @Input() collectionId = '';
  @Input() userCollectionId = '';

  /** Same as modal dismiss payload (passed to parent as `modalData.data`). */
  @Output() closed = new EventEmitter<any>();

  numBindersToOpen = 0;
  operationInProgress = false;
  collectionIconPath = '';
  /** From active collection / `collection_tag` when present. */
  collectionTitle = '';
  numSpinners = -1;
  hasImgLoaded = false;
  pcents = 0;

  /** True while waiting for active collection (or fallback API) + fin. */
  dataLoading = true;

  private activeCollectionSub?: Subscription;
  private collectionFinSub?: Subscription;

  /** Manage-style header + content reveal. */
  headerReveal = false;
  contentReveal = false;
  private headerRevealRaf1 = 0;
  private headerRevealRaf2 = 0;

  domOpen = false;
  isClosing = false;
  private closeAnimationFinishTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly OVERLAY_CLOSE_ANIM_MS = 400;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly userCollectionsService: UserCollectionsServices,
    private readonly gds: GlobalDataService,
    private readonly toastCtrl: ToastController,
    public readonly translate: TranslateService
  ) {
    addIcons({ chevronBackOutline, chevronForwardOutline });
  }

  /**
   * Binder amounts to list (same rules as legacy grid). When only 1 binder is
   * available, still returns `[1]` so the same multi-option UI is shown.
   */
  getBinderChipValues(): number[] {
    const n = this.numBindersToOpen;
    if (n < 1) return [];
    if (n === 1) return [1];
    const s = new Set<number>();
    s.add(1);
    if (n >= 2) s.add(2);
    if (n > 2 && n < 5) s.add(n);
    if (n >= 5) s.add(5);
    if (n > 5 && n < 10) s.add(n);
    if (n >= 10) s.add(10);
    return Array.from(s).sort((a, b) => a - b);
  }

  trackByChipCount(_index: number, count: number): number {
    return count;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.clearCloseAnimationFinishTimer();
        const wasClosing = this.isClosing;
        this.isClosing = false;
        this.domOpen = true;
        this.contentReveal = false;
        this.setBodyScrollLock(true);
        if (wasClosing) {
          setTimeout(() => this.resetOverlayEnterAnimationStyles(), 0);
        }
        this.scheduleHeaderRevealAnimation();
        void this.loadPageData();
      } else {
        this.clearActiveCollectionSub();
        this.cancelHeaderRevealAnimation();
        this.headerReveal = false;
        this.contentReveal = false;
        if (this.domOpen) {
          this.isClosing = true;
          this.scheduleCloseAnimationFinish();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.clearActiveCollectionSub();
    this.cancelHeaderRevealAnimation();
    this.clearCloseAnimationFinishTimer();
    this.setBodyScrollLock(false);
  }

  private loadSeq = 0;

  private clearActiveCollectionSub(): void {
    this.activeCollectionSub?.unsubscribe();
    this.activeCollectionSub = undefined;
    this.collectionFinSub?.unsubscribe();
    this.collectionFinSub = undefined;
  }

  /** Prefer GDS cache (user-collection); one API fallback if missing. */
  private async ensureCollectionFin(seq: number): Promise<void> {
    if (seq !== this.loadSeq || !this.isOpen) return;
    const snap = this.gds.getCollectionFin().getValue();
    if (snap && String(snap.collectionId) === String(this.collectionId)) {
      this.pcents = snap.binder_pcents;
      this.cdr.markForCheck();
      return;
    }
    try {
      const res: any = await this.userCollectionsService.getCollectionFin(
        this.collectionId
      );
      const row = res.resource[0];
      this.pcents = row.binder_pcents;
      this.gds.publishCollectionFin({
        collectionId: String(this.collectionId),
        binder_pcents: row.binder_pcents,
        binderNumCards: row.binderNumCards,
      });
      this.cdr.markForCheck();
    } catch (e) {
      console.log(e);
    }
  }

  /** Same row shape as `getUserCollection` / `user-collection-items` active `data.collection`. */
  private applyUserCollectionRow(row: any): void {
    this.numBindersToOpen = row.num_binders_to_open;
    this.collectionIconPath = row.collection_icon_path;
    this.numSpinners = row.num_spinners;
    const tag = row.collection_tag ?? row.collection_name ?? '';
    this.collectionTitle = typeof tag === 'string' ? tag.trim() : '';
  }

  private matchesOverlayCollection(row: any): boolean {
    if (!row?.user_collection_id) return false;
    if (String(row.user_collection_id) !== String(this.userCollectionId)) return false;
    if (this.collectionId && row.collection_id != null) {
      return String(row.collection_id) === String(this.collectionId);
    }
    return true;
  }

  private finishLoadPageData(seq: number): void {
    if (seq !== this.loadSeq || !this.isOpen) return;
    this.dataLoading = false;
    this.cdr.markForCheck();
    setTimeout(() => {
      if (!this.isOpen) return;
      this.contentReveal = true;
      this.cdr.markForCheck();
    }, 50);
  }

  private async loadPageData(): Promise<void> {
    const seq = ++this.loadSeq;
    if (!this.agentId || !this.collectionId || !this.userCollectionId) {
      this.dataLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.clearActiveCollectionSub();
    this.dataLoading = true;
    this.numBindersToOpen = 0;
    this.collectionIconPath = '';
    this.collectionTitle = '';
    this.numSpinners = -1;
    this.hasImgLoaded = false;
    this.pcents = 0;
    this.cdr.markForCheck();

    this.collectionFinSub = this.gds.getCollectionFin().subscribe((fin) => {
      if (seq !== this.loadSeq || !this.isOpen) return;
      if (fin && String(fin.collectionId) === String(this.collectionId)) {
        this.pcents = fin.binder_pcents;
        this.cdr.markForCheck();
      }
    });

    void this.ensureCollectionFin(seq);

    if (seq !== this.loadSeq || !this.isOpen) return;

    this.activeCollectionSub = this.gds.getActiveCollection().subscribe((data) => {
      if (seq !== this.loadSeq || !this.isOpen) return;
      if (!data?.collection) return;
      const row = data.collection;
      if (!this.matchesOverlayCollection(row)) return;
      this.applyUserCollectionRow(row);
      if (this.dataLoading) {
        this.finishLoadPageData(seq);
      } else {
        this.cdr.markForCheck();
      }
    });

    setTimeout(() => {
      if (seq !== this.loadSeq || !this.isOpen) return;
      if (!this.dataLoading) return;
      void this.loadUserCollectionFallback(seq);
    }, 400);
  }

  /** If active collection is not published yet (or IDs mismatch), fall back once. */
  private async loadUserCollectionFallback(seq: number): Promise<void> {
    if (seq !== this.loadSeq || !this.isOpen || !this.dataLoading) return;
    try {
      const res: any = await this.userCollectionsService.getUserCollection(
        this.userCollectionId,
        this.agentId
      );
      const row = res.resource[0];
      this.applyUserCollectionRow(row);
      this.finishLoadPageData(seq);
    } catch (err) {
      console.log(err);
      if (seq === this.loadSeq && this.isOpen) {
        this.emitDismiss({});
      }
    }
  }

  hasLoaded(): void {
    this.hasImgLoaded = true;
    this.cdr.markForCheck();
  }

  onHeaderBack(): void {
    this.emitDismiss({});
  }

  private emitDismiss(data: any): void {
    this.closed.emit(data);
  }

  /** Parent closes binders overlay and opens `app-v2-open-spinners` (same pattern as GetPage). */
  gotoOpenSpinners(): void {
    this.emitDismiss({ goto: 'OpenSpinners' });
  }

  async openBinders(num: number): Promise<void> {
    await this.openBindersInternal(num);
  }

  private async openBindersInternal(num: number): Promise<void> {
    this.operationInProgress = true;
    this.cdr.markForCheck();
    try {
      const res: any = await this.userCollectionsService.openBinders(
        this.agentId,
        this.collectionId,
        this.userCollectionId,
        num
      );
      this.operationInProgress = false;
      try {
        await this.userCollectionsService.refreshActiveCollectionFromStatus(
          this.userCollectionId,
          this.agentId.toString()
        );
      } catch (refreshErr) {
        console.error(
          'Failed to refresh active collection after opening binders',
          refreshErr
        );
      }
      res.numBindersOpen = num;
      res.source = 'OPEN_BINDERS';
      this.emitDismiss(res);
    } catch (err) {
      this.operationInProgress = false;
      const toast = await this.toastCtrl.create({
        message: this.translate.instant('ERROR_OPENNING_BINDERS'),
        duration: 3000,
        position: 'bottom',
      });
      await toast.present();
      console.log(err);
    }
    this.cdr.markForCheck();
  }

  gotoGet(): void {
    this.emitDismiss({ goto: 'GetPage' });
  }

  private scheduleHeaderRevealAnimation(): void {
    this.cancelHeaderRevealAnimation();
    this.headerReveal = false;
    this.contentReveal = false;
    this.cdr.markForCheck();
    this.headerRevealRaf1 = requestAnimationFrame(() => {
      this.headerRevealRaf1 = 0;
      this.headerRevealRaf2 = requestAnimationFrame(() => {
        this.headerRevealRaf2 = 0;
        this.headerReveal = true;
        this.cdr.markForCheck();
      });
    });
  }

  private cancelHeaderRevealAnimation(): void {
    if (this.headerRevealRaf1) {
      cancelAnimationFrame(this.headerRevealRaf1);
      this.headerRevealRaf1 = 0;
    }
    if (this.headerRevealRaf2) {
      cancelAnimationFrame(this.headerRevealRaf2);
      this.headerRevealRaf2 = 0;
    }
  }

  private getOverlayCloseAnimDurationMs(): number {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return V2OpenBindersComponent.OVERLAY_CLOSE_ANIM_MS;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : V2OpenBindersComponent.OVERLAY_CLOSE_ANIM_MS;
  }

  private clearCloseAnimationFinishTimer(): void {
    if (this.closeAnimationFinishTimer) {
      clearTimeout(this.closeAnimationFinishTimer);
      this.closeAnimationFinishTimer = null;
    }
  }

  private scheduleCloseAnimationFinish(): void {
    this.clearCloseAnimationFinishTimer();
    const ms = this.getOverlayCloseAnimDurationMs();
    this.closeAnimationFinishTimer = setTimeout(() => {
      this.closeAnimationFinishTimer = null;
      this.isClosing = false;
      this.domOpen = false;
      this.setBodyScrollLock(false);
      this.cdr.markForCheck();
    }, ms);
  }

  private resetOverlayEnterAnimationStyles(): void {
    const el = this.overlayRootEl?.nativeElement;
    if (!el) return;
    el.style.animation = 'none';
    el.style.opacity = '1';
    el.style.transform = 'scale(1)';
    void el.offsetWidth;
    el.style.removeProperty('animation');
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
  }

  private setBodyScrollLock(locked: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('v2-open-binders-open', locked);
  }
}
